import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    addDays,
    addWeeks,
    subWeeks,
    addMonths,
    subMonths,
    isSameDay,
    isSameMonth,
    isToday,
    isBefore,
    startOfDay,
    endOfDay,
    format,
    parseISO,
    differenceInMinutes,
    addMinutes,
    setHours,
    setMinutes,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'

export type CalendarView = 'day' | 'week' | 'month'

/**
 * Janela que a tela está mostrando, para buscar só esses eventos no servidor.
 *
 * Usa as MESMAS funções que desenham a grade: no mês, o calendário exibe semanas
 * inteiras e portanto invade o mês anterior e o seguinte — buscar de dia 1 a dia 30
 * deixaria as bordas vazias. O que é buscado é exatamente o que é exibido.
 *
 * Antes as listagens vinham sem recorte nenhum: a tela pedia a base inteira de
 * eventos e descartava tudo fora do mês no cliente.
 */
export function getVisibleRange(
    date: Date,
    view: CalendarView
): { startDate: string; endDate: string } {
    if (view === 'day') {
        return {
            startDate: startOfDay(date).toISOString(),
            endDate: endOfDay(date).toISOString(),
        }
    }

    if (view === 'week') {
        return {
            startDate: startOfWeek(date, { weekStartsOn: 0 }).toISOString(),
            endDate: endOfWeek(date, { weekStartsOn: 0 }).toISOString(),
        }
    }

    return {
        startDate: startOfWeek(startOfMonth(date), {
            weekStartsOn: 0,
        }).toISOString(),
        endDate: endOfWeek(endOfMonth(date), { weekStartsOn: 0 }).toISOString(),
    }
}

export interface CalendarDay {
    date: Date
    isCurrentMonth: boolean
    isToday: boolean
    isPast: boolean
    dayNumber: number
}

export function getMonthDays(date: Date): CalendarDay[] {
    const monthStart = startOfMonth(date)
    const monthEnd = endOfMonth(date)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })
    const today = startOfDay(new Date())

    return days.map(day => ({
        date: day,
        isCurrentMonth: isSameMonth(day, date),
        isToday: isToday(day),
        isPast: isBefore(day, today),
        dayNumber: day.getDate(),
    }))
}

export function getWeekDays(date: Date): CalendarDay[] {
    const weekStart = startOfWeek(date, { weekStartsOn: 0 })
    const weekEnd = endOfWeek(date, { weekStartsOn: 0 })
    const today = startOfDay(new Date())

    return eachDayOfInterval({ start: weekStart, end: weekEnd }).map(day => ({
        date: day,
        isCurrentMonth: isSameMonth(day, date),
        isToday: isToday(day),
        isPast: isBefore(day, today),
        dayNumber: day.getDate(),
    }))
}

export function generateTimeSlots(
    date: Date,
    startHour: number = 8,
    endHour: number = 18,
    slotDuration: number = 30
): Date[] {
    const slots: Date[] = []
    let currentTime = setMinutes(setHours(date, startHour), 0)
    const endTime = setMinutes(setHours(date, endHour), 0)

    while (isBefore(currentTime, endTime)) {
        slots.push(new Date(currentTime))
        currentTime = addMinutes(currentTime, slotDuration)
    }

    return slots
}

export function formatTime(date: Date): string {
    return format(date, 'HH:mm', { locale: ptBR })
}

export function formatDate(date: Date, pattern: string = 'dd/MM/yyyy'): string {
    return format(date, pattern, { locale: ptBR })
}

export function formatMonthYear(date: Date): string {
    return format(date, 'MMMM yyyy', { locale: ptBR })
}

export function formatWeekDay(date: Date): string {
    return format(date, 'EEEE', { locale: ptBR })
}

export function formatWeekDayShort(date: Date): string {
    return format(date, 'EEE', { locale: ptBR })
}

export function navigateDate(
    date: Date,
    view: CalendarView,
    direction: 'prev' | 'next'
): Date {
    switch (view) {
        case 'day':
            return direction === 'next' ? addDays(date, 1) : addDays(date, -1)
        case 'week':
            return direction === 'next' ? addWeeks(date, 1) : subWeeks(date, 1)
        case 'month':
            return direction === 'next' ? addMonths(date, 1) : subMonths(date, 1)
        default:
            return date
    }
}

export function getDateRangeForView(
    date: Date,
    view: CalendarView
): { start: Date; end: Date } {
    switch (view) {
        case 'day':
            return { start: startOfDay(date), end: endOfDay(date) }
        case 'week':
            return {
                start: startOfWeek(date, { weekStartsOn: 0 }),
                end: endOfWeek(date, { weekStartsOn: 0 }),
            }
        case 'month':
            return { start: startOfMonth(date), end: endOfMonth(date) }
        default:
            return { start: date, end: date }
    }
}

export function calculateEventPosition(
    startDate: Date,
    endDate: Date,
    dayStart: number = 8,
    dayEnd: number = 18
): { top: number; height: number } {
    const totalMinutes = (dayEnd - dayStart) * 60
    const startMinutes =
        (startDate.getHours() - dayStart) * 60 + startDate.getMinutes()
    const durationMinutes = differenceInMinutes(endDate, startDate)

    const top = Math.max(0, (startMinutes / totalMinutes) * 100)
    const height = Math.min(
        100 - top,
        (durationMinutes / totalMinutes) * 100
    )

    return { top, height }
}

export function isEventInSlot(
    eventStart: Date,
    eventEnd: Date,
    slotStart: Date,
    slotDuration: number = 30
): boolean {
    const slotEnd = addMinutes(slotStart, slotDuration)
    return isBefore(eventStart, slotEnd) && isBefore(slotStart, eventEnd)
}

export function eventsOverlap(
    event1Start: Date,
    event1End: Date,
    event2Start: Date,
    event2End: Date
): boolean {
    return isBefore(event1Start, event2End) && isBefore(event2Start, event1End)
}

export const WEEKDAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
export const WEEKDAY_FULL_NAMES = [
    'Domingo',
    'Segunda-feira',
    'Terca-feira',
    'Quarta-feira',
    'Quinta-feira',
    'Sexta-feira',
    'Sabado',
]

/**
 * Altura, em pixels, de UMA faixa de horário da grade (dia/semana).
 *
 * As views desenham cada slot com `h-[60px]`; o posicionamento dos cartões
 * precisa usar a MESMA medida, senão evento e linha do horário se descolam.
 */
export const SLOT_HEIGHT_PX = 60

/** Altura mínima de um cartão, para que um evento curto continue clicável. */
const MIN_EVENT_HEIGHT_PX = 22

export interface PositionedEvent<T> {
    event: T
    /** Deslocamento vertical em pixels a partir do topo da grade. */
    top: number
    /** Altura em pixels. */
    height: number
    /** Deslocamento horizontal em porcentagem da largura da coluna do dia. */
    left: number
    /** Largura em porcentagem da coluna do dia. */
    width: number
    /** Quantos eventos dividem a mesma faixa de horário (1 = sem concorrência). */
    columnCount: number
}

function toMinutesOfDay(date: Date): number {
    return date.getHours() * 60 + date.getMinutes()
}

/**
 * Janela de horário que a grade precisa cobrir para exibir TODOS os eventos.
 *
 * O expediente configurado (ex: 8h-18h) é só o piso: um encaixe às 7h ou um
 * procedimento que termina às 20h ficariam fora da grade e sumiriam da tela.
 * Aqui a janela é esticada até caber o primeiro e o último evento do período.
 */
export function getEffectiveHours(
    events: Array<{ startDate: string; endDate: string }>,
    workingHours: { start: number; end: number }
): { start: number; end: number } {
    let start = workingHours.start
    let end = workingHours.end

    for (const event of events) {
        const eventStart = new Date(event.startDate)
        const eventEnd = new Date(event.endDate)

        if (isNaN(eventStart.getTime())) continue

        start = Math.min(start, Math.floor(toMinutesOfDay(eventStart) / 60))

        if (!isNaN(eventEnd.getTime()) && eventEnd > eventStart) {
            const endsNextDay = !isSameDay(eventStart, eventEnd)
            const endHour = endsNextDay
                ? 24
                : Math.ceil(toMinutesOfDay(eventEnd) / 60)
            end = Math.max(end, endHour)
        } else {
            end = Math.max(end, Math.ceil(toMinutesOfDay(eventStart) / 60) + 1)
        }
    }

    return { start: Math.max(0, start), end: Math.min(24, Math.max(end, start + 1)) }
}

/** Intervalo de um evento em minutos do dia, já recortado à janela da grade. */
export interface EventInterval {
    startMinutes: number
    endMinutes: number
}

/**
 * Converte um evento em minutos do dia, recortado à janela visível.
 *
 * Evento sem fim válido ocupa um slot; evento que vira o dia é cortado no fim
 * da grade — a grade desenha UM dia.
 */
function toMinuteInterval(
    event: { startDate: string; endDate: string },
    hours: { start: number; end: number },
    slotDuration: number
): EventInterval | null {
    const start = new Date(event.startDate)
    const end = new Date(event.endDate)

    if (isNaN(start.getTime())) return null

    const gridStart = hours.start * 60
    const gridEnd = hours.end * 60

    const startMinutes = Math.max(
        gridStart,
        Math.min(toMinutesOfDay(start), gridEnd)
    )
    const hasValidEnd = !isNaN(end.getTime()) && end > start
    const endsNextDay = hasValidEnd && !isSameDay(start, end)
    const rawEnd = !hasValidEnd
        ? toMinutesOfDay(start) + slotDuration
        : endsNextDay
          ? gridEnd
          : toMinutesOfDay(end)

    return {
        startMinutes,
        endMinutes: Math.max(startMinutes + 1, Math.min(rawEnd, gridEnd)),
    }
}

export function getEventIntervals(
    events: Array<{ startDate: string; endDate: string }>,
    hours: { start: number; end: number },
    slotDuration: number
): EventInterval[] {
    return events
        .map(event => toMinuteInterval(event, hours, slotDuration))
        .filter((interval): interval is EventInterval => interval !== null)
}

/** Altura da faixa que representa um intervalo de horários recolhido. */
export const COLLAPSED_GAP_HEIGHT_PX = 34

/** Altura da alça que devolve ao estado recolhido um trecho vazio aberto. */
export const COLLAPSE_HANDLE_HEIGHT_PX = 18

/**
 * Quantos slots vazios seguidos justificam recolher.
 *
 * Recolher um slot solitário não encurta nada e ainda quebra a leitura da
 * régua de horários.
 */
const MIN_SLOTS_TO_COLLAPSE = 2

export interface GridSegment {
    /** `gap` é um intervalo sem nenhum evento, desenhado como faixa fina. */
    type: 'slots' | 'gap'
    /** Identificador estável do trecho, usado para lembrar o que foi aberto. */
    key: string
    startMinutes: number
    endMinutes: number
    /** Os slots do trecho — no `gap`, os que estão escondidos. */
    slots: Date[]
    /**
     * Trecho vazio que o operador abriu: continua recolhível, e por isso ganha
     * a alça de fechar no topo.
     */
    collapsible: boolean
    /** Altura do trecho inteiro, em pixels. */
    height: number
}

/**
 * Divide a grade em trechos com evento (altura cheia) e trechos vazios
 * (recolhidos numa faixa).
 *
 * O dia útil tem 10 horas e o operador costuma ter compromisso em três ou
 * quatro: exibir a régua inteira obriga a rolar por horas vazias para descobrir
 * que existe algo às 17h. Os vazios continuam ali, recolhidos — clicar abre o
 * trecho e devolve os slots, porque é por eles que se cria um agendamento.
 */
export function buildGridSegments(
    slots: Date[],
    intervals: EventInterval[],
    slotDuration: number,
    expandedKeys: ReadonlySet<string> = new Set()
): GridSegment[] {
    const isSlotBusy = (slot: Date): boolean => {
        const slotStart = toMinutesOfDay(slot)
        const slotEnd = slotStart + slotDuration
        return intervals.some(
            interval =>
                interval.startMinutes < slotEnd &&
                slotStart < interval.endMinutes
        )
    }

    const segments: GridSegment[] = []
    let current: Date[] = []
    let currentBusy = false

    const flush = () => {
        if (current.length === 0) return

        const startMinutes = toMinutesOfDay(current[0])
        const endMinutes =
            toMinutesOfDay(current[current.length - 1]) + slotDuration
        const key = `${startMinutes}-${endMinutes}`
        const collapsible =
            !currentBusy && current.length >= MIN_SLOTS_TO_COLLAPSE
        const collapsed = collapsible && !expandedKeys.has(key)

        segments.push({
            type: collapsed ? 'gap' : 'slots',
            key,
            startMinutes,
            endMinutes,
            slots: current,
            collapsible,
            height: collapsed
                ? COLLAPSED_GAP_HEIGHT_PX
                : current.length * SLOT_HEIGHT_PX +
                  (collapsible ? COLLAPSE_HANDLE_HEIGHT_PX : 0),
        })

        current = []
    }

    for (const slot of slots) {
        const busy = isSlotBusy(slot)

        if (current.length > 0 && busy !== currentBusy) {
            flush()
        }

        currentBusy = busy
        current.push(slot)
    }

    flush()

    return segments
}

export function getGridHeight(segments: GridSegment[]): number {
    return segments.reduce((total, segment) => total + segment.height, 0)
}

/**
 * Mapa minuto do dia -> pixel, ciente dos trechos recolhidos.
 *
 * Com faixas recolhidas a grade deixa de ser linear: cada trecho tem a sua
 * própria escala, e um cartão posicionado pela regra antiga apareceria horas
 * fora do lugar.
 */
export function createMinutesToPixels(
    segments: GridSegment[],
    slotDuration: number
): (minutes: number) => number {
    return (minutes: number): number => {
        let offset = 0

        for (const segment of segments) {
            if (minutes >= segment.endMinutes) {
                offset += segment.height
                continue
            }

            if (minutes <= segment.startMinutes) return offset

            const ratio =
                (minutes - segment.startMinutes) /
                Math.max(1, segment.endMinutes - segment.startMinutes)

            return offset + ratio * segment.height
        }

        return offset
    }
}

/**
 * Posiciona os eventos de UM dia na grade, distribuindo em colunas o que
 * acontece no mesmo horário.
 *
 * Sem isso todo cartão nascia com a mesma largura e a mesma posição horizontal:
 * dois atendimentos às 09h ficavam exatamente um EM CIMA do outro, e a tela dava
 * a impressão de haver uma consulta só. A distribuição segue o padrão dos
 * calendários: eventos que se cruzam formam um grupo, cada um recebe a primeira
 * coluna livre e o grupo inteiro divide a largura disponível.
 *
 * A posição é calculada em PIXELS (a partir de `SLOT_HEIGHT_PX`), não em
 * porcentagem — o container dos cartões é absoluto e não tem altura própria,
 * então porcentagem colapsava tudo para o topo com altura zero.
 */
export function layoutDayEvents<T extends { startDate: string; endDate: string }>(
    events: T[],
    hours: { start: number; end: number },
    slotDuration: number = 30,
    minutesToPixels?: (minutes: number) => number
): PositionedEvent<T>[] {
    const pixelsPerMinute = SLOT_HEIGHT_PX / Math.max(1, slotDuration)
    const gridStart = hours.start * 60
    const toPixels =
        minutesToPixels ??
        ((minutes: number) => (minutes - gridStart) * pixelsPerMinute)

    interface Span extends EventInterval {
        event: T
        column: number
    }

    const spans: Span[] = events
        .map(event => {
            const interval = toMinuteInterval(event, hours, slotDuration)
            return interval ? { event, ...interval, column: 0 } : null
        })
        .filter((span): span is Span => span !== null)
        .sort((a, b) =>
            a.startMinutes !== b.startMinutes
                ? a.startMinutes - b.startMinutes
                : b.endMinutes - a.endMinutes
        )

    const positioned: PositionedEvent<T>[] = []

    // Um "cluster" é uma sequência de eventos encadeados por sobreposição; só os
    // eventos do mesmo cluster dividem largura entre si.
    let cluster: Span[] = []
    let clusterEnd = -1

    const flushCluster = () => {
        if (cluster.length === 0) return

        const columnCount = Math.max(...cluster.map(span => span.column)) + 1

        for (const span of cluster) {
            const top = toPixels(span.startMinutes)
            // A altura mínima só vale para o desenho: a concorrência já foi
            // resolvida pela duração real, então um evento curto não "rouba"
            // coluna de quem vem logo depois.
            const height = Math.max(
                MIN_EVENT_HEIGHT_PX,
                toPixels(span.endMinutes) - top
            )

            positioned.push({
                event: span.event,
                top,
                height,
                left: (span.column * 100) / columnCount,
                width: 100 / columnCount,
                columnCount,
            })
        }

        cluster = []
        clusterEnd = -1
    }

    // Último fim de cada coluna aberta no cluster corrente.
    let columnEnds: number[] = []

    for (const span of spans) {
        if (span.startMinutes >= clusterEnd && cluster.length > 0) {
            flushCluster()
            columnEnds = []
        }

        let column = columnEnds.findIndex(end => end <= span.startMinutes)
        if (column === -1) {
            column = columnEnds.length
        }

        columnEnds[column] = span.endMinutes
        span.column = column

        cluster.push(span)
        clusterEnd = Math.max(clusterEnd, span.endMinutes)
    }

    flushCluster()

    return positioned
}

/** Eventos que começam no dia informado. */
export function getEventsForDay<T extends { startDate: string }>(
    events: T[],
    date: Date
): T[] {
    return events.filter(event => {
        const eventDate = new Date(event.startDate)
        return !isNaN(eventDate.getTime()) && isSameDay(eventDate, date)
    })
}
